import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { map, mergeMap, of, switchMap } from 'rxjs';

import { EffectsBase } from '@ps/core-data-access';

import { SurveyService } from '../../services/survey/survey.service';
import { PsidService } from '../../services/psid/psid.service';
import {
  ManageSurveyActions,
  ManageSurveyApiActions,
} from './manage-survey.actions';
import { selectQuotaLoadedPage } from './manage-survey.selectors';

@Injectable()
export class ManageSurveyEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _surveyService = inject(SurveyService);
  private readonly _psidService = inject(PsidService);

  public loadSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.loadSurveyDetails),
      switchMap(({ surveyId }) =>
        this._surveyService.getById(surveyId).pipe(
          map((survey) => ManageSurveyApiActions.loadSurveySuccess({ survey })),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.loadSurveyFailure({
                  error: err.message ?? 'Failed to load survey',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public loadChildSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.loadChildSurveyDetails),
      switchMap(({ surveyId }) =>
        this._surveyService.getById(surveyId, 0, true).pipe(
          map((survey) =>
            ManageSurveyApiActions.loadChildSurveySuccess({ survey }),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.loadChildSurveyFailure({
                  error: err.message ?? 'Failed to load survey',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public loadSurveyQuotas$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.loadSurveyQuotas),
      switchMap(({ surveyId, page }) =>
        this._surveyService.getQuotas(surveyId, page).pipe(
          map(({ quotas, total }) =>
            ManageSurveyApiActions.loadSurveyQuotasSuccess({ quotas, total }),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.loadSurveyQuotasFailure({
                  error: err.message ?? 'Failed to load quotas',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public readonly updateCurrentCpi$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.updateCurrentCpi),
      switchMap(({ surveyId, cpi, isChild }) =>
        this._surveyService
          .updateCurrentCpi(surveyId, cpi, isChild, 'drawer')
          .pipe(
            map((survey) =>
              isChild
                ? ManageSurveyApiActions.updateChildCurrentCpiSuccess({
                    survey,
                  })
                : ManageSurveyApiActions.updateCurrentCpiSuccess({ survey }),
            ),
            this.psCatchError(
              (err) =>
                of(
                  ManageSurveyApiActions.updateCurrentCpiFailure({
                    error: err.message ?? 'Failed to update CPI',
                  }),
                ),
              { notify: true },
            ),
          ),
      ),
    ),
  );

  public loadMoreQuotas$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.loadMoreQuotas),
      concatLatestFrom(() => this._store.select(selectQuotaLoadedPage)),
      switchMap(([{ surveyId }, page]) =>
        this._surveyService.getQuotas(surveyId, page).pipe(
          map(({ quotas }) =>
            ManageSurveyApiActions.loadMoreQuotasSuccess({ quotas }),
          ),
          this.psCatchError(
            () => of(ManageSurveyApiActions.loadMoreQuotasFailure()),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public loadAllQuotas$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.loadAllQuotas),
      switchMap(({ surveyId }) =>
        this._surveyService.getQuotas(surveyId).pipe(
          map(({ quotas }) =>
            ManageSurveyApiActions.loadAllQuotasSuccess({ quotas }),
          ),
          this.psCatchError(
            () => of(ManageSurveyApiActions.loadAllQuotasFailure()),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public setQuotaLock$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.setQuotaLock),
      mergeMap(({ surveyId, psQuotaId, locked }) =>
        this._surveyService.setQuotaLock(surveyId, psQuotaId, locked).pipe(
          map(() =>
            ManageSurveyApiActions.setQuotaLockSuccess({ psQuotaId, locked }),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.setQuotaLockFailure({
                  psQuotaId,
                  error: err.message ?? 'Failed to update quota lock',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public setQuotaCurrentTarget$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.setQuotaCurrentTarget),
      mergeMap(({ surveyId, psQuotaId, currentTarget, updateSupplier }) =>
        this._surveyService
          .setQuotaCurrentTarget(
            surveyId,
            psQuotaId,
            currentTarget,
            updateSupplier,
          )
          .pipe(
            map((update) =>
              ManageSurveyApiActions.setQuotaCurrentTargetSuccess({
                psQuotaId,
                update,
              }),
            ),
            this.psCatchError(
              (err) =>
                of(
                  ManageSurveyApiActions.setQuotaCurrentTargetFailure({
                    psQuotaId,
                    error: err.message ?? 'Failed to update quota target',
                  }),
                ),
              { notify: true },
            ),
          ),
      ),
    ),
  );

  public loadTrafficChannels$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.loadTrafficChannels),
      switchMap(({ surveyId }) =>
        this._surveyService.getTrafficChannelChildren(surveyId).pipe(
          map((trafficChannels) =>
            ManageSurveyApiActions.loadTrafficChannelsSuccess({
              trafficChannels,
            }),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.loadTrafficChannelsFailure({
                  error: err.message ?? 'Failed to load traffic channels',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public setTrafficChannelStatus$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.setTrafficChannelStatus),
      mergeMap(({ surveyId, status }) =>
        this._surveyService.updateSurveyStatus(surveyId, status).pipe(
          map(() =>
            ManageSurveyApiActions.setTrafficChannelStatusSuccess({
              surveyId,
              status,
            }),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.setTrafficChannelStatusFailure({
                  surveyId,
                  error:
                    err.message ?? 'Failed to update traffic channel status',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public launchTrafficChannel$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.launchTrafficChannel),
      mergeMap(({ surveyId, event }) =>
        this._surveyService.launchTrafficChannel(surveyId, event).pipe(
          map((trafficChannels) =>
            ManageSurveyApiActions.launchTrafficChannelSuccess({
              surveyId,
              trafficChannels,
            }),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.launchTrafficChannelFailure({
                  surveyId,
                  error: err.message ?? 'Failed to launch traffic channel',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public uploadPsidFile$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.uploadPsidFile),
      switchMap(({ surveyId, file }) =>
        this._psidService.uploadPsidFile(surveyId, file).pipe(
          map((result) => ManageSurveyApiActions.uploadPsidFileSuccess(result)),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.uploadPsidFileFailure({
                  error: err.message ?? 'Failed to upload respondent list',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public downloadRespondentListTemplate$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.downloadRespondentListTemplate),
      switchMap(() =>
        this._surveyService.downloadRespondentListTemplate().pipe(
          map(() =>
            ManageSurveyApiActions.downloadRespondentListTemplateSuccess(),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.downloadRespondentListTemplateFailure({
                  error: err.message ?? 'Failed to download template',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );
}
